<template>
  <v-card outlined class="mb-4 salary-branch-card">
    <v-card-text>
      <v-row align="center" class="mb-3">
        <v-col cols="auto">
          <v-icon color="amber darken-2" size="32">mdi-cash-multiple</v-icon>
        </v-col>
        <v-col>
          <div class="text-h6 font-weight-bold amber--text text--darken-2">
            ข้อมูลเงินเดือนและสาขาวิชา
          </div>
        </v-col>
      </v-row>

      <v-row>
        <!-- เงินเดือนปัจจุบัน -->
        <v-col cols="12" md="4">
          <v-text-field
            v-model.number="localData.salary_s"
            type="number"
            outlined
            dense
            label="เงินเดือนปัจจุบัน"
            prepend-inner-icon="mdi-currency-usd"
            placeholder="ระบุจำนวนเงินเดือน"
            suffix="บาท"
            :rules="salaryRules"
            hint="เงินเดือนที่ได้รับในปัจจุบัน"
            persistent-hint
          >
            <template v-slot:append>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                    small
                    color="info"
                  >
                    mdi-information
                  </v-icon>
                </template>
                <span>เงินเดือนที่ได้รับ ไม่รวมเงินเพิ่มอื่นๆ</span>
              </v-tooltip>
            </template>
          </v-text-field>

          <!-- แสดงข้อมูลเงินเดือน -->
          <v-fade-transition>
            <v-alert
              v-if="formattedSalary"
              dense
              outlined
              type="info"
              icon="mdi-calculator"
              class="mt-2"
            >
              <div class="text-caption">
                <strong>เงินเดือน:</strong> {{ formattedSalary }}
              </div>
              <div class="text-caption" v-if="annualSalary">
                <strong>เงินเดือนต่อปี:</strong> {{ annualSalary }}
              </div>
            </v-alert>
          </v-fade-transition>
        </v-col>

        <!-- สาขาวิชาที่เกี่ยวข้อง -->
        <v-col cols="12" md="8">
          <v-autocomplete
            v-model="localData.pt_id_branch"
            outlined
            dense
            label="สาขาวิชาที่เกี่ยวข้อง"
            prepend-inner-icon="mdi-book-open-page-variant"
            :items="branches"
            item-text="name_branch"
            item-value="id_branch"
            :rules="branchRules"
            placeholder="เลือกสาขาวิชาที่สอน"
            hint="เลือกสาขาวิชาที่ตรงกับการสอนหรือความเชี่ยวชาญ"
            persistent-hint
            clearable
            :search-input.sync="branchSearch"
          >
            <!-- แสดงรายการในดรอปดาวน์ -->
            <template v-slot:item="{ item }">
              <v-list-item-avatar color="amber lighten-4" size="36">
                <v-icon color="amber darken-3">mdi-book-open-variant</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.name_branch }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <v-chip x-small color="amber" text-color="white">
                    รหัส: {{ item.id_branch }}
                  </v-chip>
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>

            <!-- แสดงค่าที่เลือก -->
            <template v-slot:selection="{ item }">
              <v-chip
                small
                color="amber darken-2"
                text-color="white"
              >
                <v-icon left small>mdi-book-open-variant</v-icon>
                {{ item.id_branch }}: {{ item.name_branch }}
              </v-chip>
            </template>

            <!-- ข้อความเมื่อไม่มีข้อมูล -->
            <template v-slot:no-data>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title class="text-center">
                    <v-icon left>mdi-magnify</v-icon>
                    ไม่พบสาขาวิชาที่ตรงกับการค้นหา
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>

            <!-- แสดงจำนวนรายการ -->
            <template v-slot:append-outer>
              <v-chip
                small
                outlined
                color="amber darken-2"
              >
                <v-icon left x-small>mdi-format-list-numbered</v-icon>
                {{ branches.length }}
              </v-chip>
            </template>
          </v-autocomplete>

          <!-- แสดงข้อมูลสาขาที่เลือก -->
          <v-fade-transition>
            <v-card
              v-if="selectedBranch"
              outlined
              class="mt-2 selected-branch-card"
            >
              <v-card-text class="py-2">
                <v-row dense align="center">
                  <v-col cols="auto">
                    <v-avatar color="amber lighten-4" size="40">
                      <v-icon color="amber darken-3">mdi-check-circle</v-icon>
                    </v-avatar>
                  </v-col>
                  <v-col>
                    <div class="font-weight-bold text-body-2">
                      สาขาวิชาที่เลือก
                    </div>
                    <div class="text-caption grey--text">
                      {{ selectedBranch.name_branch }}
                    </div>
                  </v-col>
                  <v-col cols="auto">
                    <v-chip small color="success" outlined>
                      <v-icon left x-small>mdi-check</v-icon>
                      เลือกแล้ว
                    </v-chip>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-fade-transition>
        </v-col>
      </v-row>

      <!-- คำแนะนำ -->
      <v-row>
        <v-col cols="12">
          <v-expansion-panels flat>
            <v-expansion-panel>
              <v-expansion-panel-header color="amber lighten-5">
                <div>
                  <v-icon small left color="amber darken-2">mdi-information-outline</v-icon>
                  <span class="text-caption font-weight-bold">
                    คำแนะนำการกรอกข้อมูล
                  </span>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="text-subtitle-2 font-weight-bold mb-2">
                      💰 เงินเดือน
                    </div>
                    <v-list dense>
                      <v-list-item>
                        <v-list-item-icon>
                          <v-icon small color="amber darken-2">mdi-check-circle</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="text-caption">
                            กรอกเงินเดือนที่ได้รับจริง (ไม่รวมเงินเพิ่มอื่นๆ)
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-icon>
                          <v-icon small color="amber darken-2">mdi-check-circle</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="text-caption">
                            เงินเดือนต้องไม่น้อยกว่า 15,000 บาท
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-icon>
                          <v-icon small color="amber darken-2">mdi-check-circle</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="text-caption">
                            กรอกเป็นตัวเลขเท่านั้น ไม่ต้องใส่จุด หรือ คอมม่า
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="text-subtitle-2 font-weight-bold mb-2">
                      📚 สาขาวิชา
                    </div>
                    <v-list dense>
                      <v-list-item>
                        <v-list-item-icon>
                          <v-icon small color="amber darken-2">mdi-check-circle</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="text-caption">
                            เลือกสาขาวิชาที่ตรงกับงานที่รับผิดชอบ
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-icon>
                          <v-icon small color="amber darken-2">mdi-check-circle</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="text-caption">
                            สามารถค้นหาสาขาวิชาได้โดยพิมพ์ชื่อหรือรหัสสาขา
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-icon>
                          <v-icon small color="amber darken-2">mdi-check-circle</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="text-caption">
                            หากไม่พบสาขาที่ต้องการ แจ้งเจ้าหน้าที่เพื่อเพิ่มในระบบ
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "SalaryBranchSection",
  
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    branches: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      branchSearch: "",

      // กฎการตรวจสอบ
      salaryRules: [
        v => !!v || "กรุณากรอกเงินเดือน",
        v => v >= 15000 || "เงินเดือนต้องไม่น้อยกว่า 15,000 บาท",
        v => v <= 200000 || "เงินเดือนต้องไม่เกิน 200,000 บาท",
        v => Number.isInteger(Number(v)) || "กรอกเป็นตัวเลขจำนวนเต็มเท่านั้น"
      ],

      branchRules: [
        v => !!v || "กรุณาเลือกสาขาวิชาที่เกี่ยวข้อง"
      ]
    };
  },

  computed: {
    localData: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },

    // จัดรูปแบบเงินเดือน
    formattedSalary() {
      if (!this.localData.salary_s) return "";
      return new Intl.NumberFormat('th-TH', {
        style: 'currency',
        currency: 'THB'
      }).format(this.localData.salary_s);
    },

    // คำนวณเงินเดือนต่อปี
    annualSalary() {
      if (!this.localData.salary_s) return "";
      const annual = this.localData.salary_s * 12;
      return new Intl.NumberFormat('th-TH', {
        style: 'currency',
        currency: 'THB'
      }).format(annual);
    },

    // ข้อมูลสาขาที่เลือก
    selectedBranch() {
      if (!this.localData.pt_id_branch) return null;
      return this.branches.find(
        branch => branch.id_branch === this.localData.pt_id_branch
      );
    }
  },

  watch: {
    // ตรวจสอบเงินเดือนเมื่อมีการเปลี่ยนแปลง
    'localData.salary_s': function(newVal) {
      if (newVal && newVal < 15000) {
        this.$emit('salary-warning', 'เงินเดือนต่ำกว่าเกณฑ์ขั้นต่ำ');
      }
    }
  }
};
</script>

<style scoped>
.salary-branch-card {
  transition: all 0.3s ease;
  border-radius: 12px;
  border: 2px solid #fff8e1;
}

.salary-branch-card:hover {
  box-shadow: 0 6px 24px rgba(255, 160, 0, 0.15);
  border-color: #ffd54f;
}

.selected-branch-card {
  border-radius: 10px;
  background: linear-gradient(to right, #fff8e1, #fffde7);
  border: 2px solid #ffca28;
  transition: all 0.3s ease;
}

.selected-branch-card:hover {
  box-shadow: 0 4px 12px rgba(255, 202, 40, 0.3);
}

/* Custom styling */
::v-deep .v-text-field--outlined,
::v-deep .v-autocomplete--outlined {
  border-radius: 8px;
}

::v-deep .v-input__icon--prepend-inner i {
  color: #ff8f00 !important;
}

::v-deep .v-alert {
  border-radius: 10px;
}

::v-deep .v-expansion-panel {
  border-radius: 8px !important;
  border: 1px solid #ffe082;
}

::v-deep .v-chip {
  border-radius: 8px !important;
}

/* Autocomplete list styling */
::v-deep .v-list-item {
  transition: background-color 0.2s ease;
}

::v-deep .v-list-item:hover {
  background-color: #fff8e1 !important;
}

/* Animation */
.v-fade-transition-enter-active,
.v-fade-transition-leave-active {
  transition: opacity 0.3s ease;
}

.v-fade-transition-enter,
.v-fade-transition-leave-to {
  opacity: 0;
}
</style>